/**
 * Main App component that sets up the application routing and authentication context.
 * This component serves as the root layout for the entire application.
/**
 * Main App component that sets up the application routing and authentication context.
 * This component serves as the root layout for the entire application.
 * Includes routes for home, login, profile, details, and admin dashboard.
 */

import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./auth/AuthProvider";
import { msalInstance } from "./auth/msalConfig";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Profile from "./pages/Profile";
import ProtectedRoute from "./components/ProtectedRout";
import Details from "./components/Details";
import "./i18n";
import AdminLayout from "./components/admin/AdminLayout";
import AdminBuildingsPage from "./components/admin/AdminBuildingsPage";
import AdminRealtimePage from "./components/admin/AdminRealtimePage";
import AdminCameraPage from "./components/admin/AdminCameraPage";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import Contact from "./pages/Contact";
/**
 * Main App component that wraps the application with authentication and routing.
 * @returns {JSX.Element} The rendered App component
 */
const AppRoutes = () => {
  const location = useLocation();
  const state = location.state || {};
  const backgroundLocation = state.backgroundLocation;

  return (
    <>
      <Routes location={backgroundLocation || location}>
        <Route path="/" element={<Home />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfileLayout />
            </ProtectedRoute>
          }
        />
        <Route path="/details/:guid" element={<DetailsLayout />} />
        <Route
          path="/combinedDetail/:Bguid"
          element={<CombinedDetailsLayout />}
        />
        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <AdminPortalLayout />
            </AdminProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/buildings" replace />} />
          <Route path="buildings" element={<AdminBuildingsPage />} />
          <Route path="realtime" element={<AdminRealtimePage />} />
          <Route path="camera" element={<AdminCameraPage />} />
        </Route>
      </Routes>

      {backgroundLocation && (
        <Routes>
          <Route path="/contact" element={<Contact isModal />} />
        </Routes>
      )}
    </>
  );
};

/** Renders global Navbar only when not on admin (admin has its own top bar in the right column). */
const NavbarWhenNotAdmin = () => {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  return <Navbar />;
};

const App = () => {
  return (
    <AuthProvider instance={msalInstance}>
      <Router>
        <NavbarWhenNotAdmin />
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
};

/**
 * Layout component for the Profile page (Navbar is rendered once in App).
 * @returns {JSX.Element} The rendered ProfileLayout component
 */
const ProfileLayout = () => {
  return (
    <div className="pt-14">
      <Profile />
    </div>
  );
};

/**
 * Layout component for the Details page (Navbar is rendered once in App).
 * @returns {JSX.Element} The rendered DetailsLayout component
 */
const DetailsLayout = () => {
  return (
    <div className="pt-14">
      <Details />
    </div>
  );
};

/**
 * Layout component for the Combined Details page (Navbar is rendered once in App).
 * @returns {JSX.Element} The rendered CombinedDetailsLayout component
 */
const CombinedDetailsLayout = () => {
  return (
    <div className="pt-14">
      <Details />
    </div>
  );
};

const AdminPortalLayout = () => {
  return <AdminLayout />;
};

export default App;
